import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from "rxjs/operators";

import {
  AccountingCloseTaskStatus,
  BLOB_RESPONSE_TYPE,
  ChartOfAccount,
  CommonTransformationService,
  FiscalYearCostPool,
  IndirectRateSummary,
  LedgerAccount,
  ProvisionalRateChange,
  QueryResult
} from '@app/shared';

import {UserService} from '@app/core';
import {CostPoolSearch, LedgerAccountSearch, StatementSearch, TrialBalance, TrialBalanceSearch} from './general-ledger';
import {JournalEntry, JournalEntrySearch, JournalEntrySourceType} from './journal-entry/journal-entry';
import {SelectItem, TreeNode} from "primeng/api";

@Injectable()
export class GeneralLedgerDataService {

  constructor(private http: HttpClient, private user: UserService, private transform: CommonTransformationService) {
  }

  getFiscalYearMonth(): Observable<any> {
    let url = `/accounting/fiscal-year-month?`;
    return this.http.get<any>(url);
  }

  getLedgerAccounts(): Observable<QueryResult> {
    let url = `/accounting/fy-ledger-accounts/search?active=true`;
    return this.http.get<QueryResult>(url);
  }

  getLedgerAccountById(accountId: number, active?: boolean): Observable<QueryResult> {
    let url = `/accounting/fy-ledger-accounts/search?fiscalYearLedgerAccountId=${accountId}&active=${active}`;
    return this.http.get<QueryResult>(url);
  }

  getChartOfAccounts(): Observable<TreeNode<ChartOfAccount>[]> {
    let url = `/finance/accounts?`;
    return this.http.get<any>(url).pipe(map(res => {
      return this.transform.convertToTree("accountName", res);
    }));
  }

  getAccountsByAccountTypeId(accountTypeId: number): Observable<SelectItem[]> {
    let url = `/finance/accounts/account-type/searchById?accountTypeId=${accountTypeId}`;
    return this.http.get<QueryResult>(url).pipe(map(res => {
      return this.transform.convertToDropDown("accountName", res.list, "id", true);
    }));
  }

  getAccountingPeriods(year: number | string): Observable<QueryResult> {
    let url = `/accounting/general-ledger/calendars?year=${year}`;
    return this.http.get<QueryResult>(url);
  }

  getLedgerBalances(): Observable<QueryResult> {
    let url = `/accounting/general-ledger/balances?active=true`;
    return this.http.get<QueryResult>(url);
  }

  calculateIndirectRate(indirectRateType): Observable<FiscalYearCostPool[]> {
    let url = `/accounting/calculate-indirect-rate/${indirectRateType}`;
    return this.http.get<FiscalYearCostPool[]>(url);
  }

  calculateLaborAtActualAndForeCast(accountingPeriodId: number): Observable<any> {
    let url = `/accounting/general-ledger/segregated-cost?accountingPeriodId=${accountingPeriodId}`;
    return this.http.post<any[]>(url, {});
  }

  calculateExpenseAtActualAndForeCast(accountingPeriodId: number): Observable<any> {
    let url = `/accounting/period-expense/segregated-cost?accountingPeriodId=${accountingPeriodId}`;
    return this.http.post<any[]>(url, {});
  }


  getLedgerBalancesWithSearchParams(periodBalanceSearch): Observable<QueryResult> {
    let query: string = this.transform.buildSearchPanelURL(periodBalanceSearch);
    return this.http.get<QueryResult>(`/accounting/general-ledger/balances?` + query);
  }

  updateAccountBudgets(ledgerAccount: LedgerAccount): Observable<any> {
    let url = `/accounting/ledger-accounts/${ledgerAccount.id}?`;
    return this.http.put<any>(url, ledgerAccount);
  }

  searchLedgerAccounts(ledgerAccountSearch: LedgerAccountSearch): Observable<QueryResult> {
    let query: string = this.transform.buildSearchPanelURL(ledgerAccountSearch);
    return this.http.get<QueryResult>(`/accounting/fy-ledger-accounts/search?` + query);
  }

  getJournalEntry(journalEntryId: number): Observable<JournalEntry> {
    let url = `/accounting/journal-entry/${journalEntryId}`;
    return this.http.get<JournalEntry>(url);
  }

  getJournalEntries(): Observable<QueryResult> {
    let url = `/accounting/journal-entry/search?`;
    return this.http.get<QueryResult>(url);
  }

  saveJournalEntry(journalEntry: JournalEntry): Observable<JournalEntry> {
    let url = `/accounting/journal-entry?`;
    return this.http.post<JournalEntry>(url, journalEntry);
  }

  updateJournalEntry(journalEntry: JournalEntry): Observable<JournalEntry> {
    let url = `/accounting/journal-entry/${journalEntry.id}?`;
    return this.http.put<JournalEntry>(url, journalEntry);
  }

  deleteJournalEntry(journalEntryId: number): Observable<Response> {
    let url = `/accounting/journal-entry/${journalEntryId}?`;
    return this.http.delete<Response>(url);
  }

  getRecurringJournalEntries() {
    let url = `/accounting/journal-entry/recurring`;
    return this.http.get<any>(url);
  }

  getTemplateJournalEntries(): Observable<QueryResult> {
    let url = `/accounting/journal-entry/templates`;
    return this.http.get<QueryResult>(url);
  }

  getNonRecurringTemplateJournalEntries() {
    let url = `/accounting/journal-entry/non-recurring-templates`;
    return this.http.get<any>(url).pipe(map(res => {
      return this.transform.convertToDropDown("name", res['list']);
    }));
  }

  searchJournalEntry(journalEntry: JournalEntrySearch): Observable<QueryResult> {
    let query: string = this.transform.buildSearchPanelURL(journalEntry);
    return this.http.get<QueryResult>(`/accounting/journal-entry/search?` + query);
  }

  searchJournalEntryByAuditCorrectionProcessingById(auditCorrectionJournalGroupId: number): Observable<QueryResult> {
    let url = `/accounting/journal-entry/search?auditCorrectionJournalGroupId=${auditCorrectionJournalGroupId}`;
    return this.http.get<QueryResult>(url);
  }

  getLandingTrialBalances(postAccountingPeriodId: number, onlyPostedEntries?: boolean): Observable<QueryResult> {

    let url = `/accounting/general-ledger/trial-balances/search?onlyPostedEntries=${onlyPostedEntries}`;
    if (postAccountingPeriodId != null) {
      url = url + `&postAccountingPeriodId=` + postAccountingPeriodId;
    }
    return this.http.get<QueryResult>(url);
  }

  getLastPostedPeriodLandingTrialBalances(postAccountingPeriodId: number, onlyPostedEntries?: boolean): Observable<TrialBalance[]> {

    let url = `/accounting/general-ledger/trial-balances-last-closed-period?onlyPostedEntries=${onlyPostedEntries}`;
    if (postAccountingPeriodId != null) {
      url = url + `&periodId=` + postAccountingPeriodId;
    }
    return this.http.get<TrialBalance[]>(url);
  }

  searchTrialBalances(trialBalanceSearch: TrialBalanceSearch): Observable<QueryResult> {
    let query: string = this.transform.buildSearchPanelURL(trialBalanceSearch);
    return this.http.get<QueryResult>(`/accounting/general-ledger/trial-balances/search?` + query);
  }

  getUnprocessedLedgerEntries(postAccountingPeriodId?: number): Observable<QueryResult> {
    let url = `/accounting/general-ledger/unprocessed`;
    if (postAccountingPeriodId != null) {
      url = url + `?postAccountingPeriodId=` + postAccountingPeriodId;
    }
    return this.http.get<QueryResult>(url);
  }

  saveTrialBalance(): Observable<any> {
    let url = `/accounting/general-ledger/trial-balances`;
    return this.http.post<any>(url, null);
  }

  saveTrialBalancesToGeneralLedger(postAccountingPeriodId?: number): Observable<any> {
    let emptyBody: any;
    let url = `/accounting/general-ledger/trial-balances`;
    if (postAccountingPeriodId != null) {
      url = url + `?postAccountingPeriodId=` + postAccountingPeriodId;
    }
    return this.http.post<any>(url, emptyBody);
  }

  saveCorrectionTrialBalancesToGeneralLedger(journalEntryType: JournalEntrySourceType , postAccountingPeriodId?: number): Observable<any> {
    let emptyBody: any;
    let url = `/accounting/general-ledger/trial-balances/correction-audit?journalEntryType=${journalEntryType}`;
    if (postAccountingPeriodId != null) {
      url = url + `&postAccountingPeriodId=` + postAccountingPeriodId;
    }
    return this.http.post<any>(url, emptyBody);
  }

  saveAuditTrialBalancesToGeneralLedger(journalEntryType: JournalEntrySourceType, postAccountingPeriodId?: number): Observable<any> {
    let emptyBody: any;
    let url = `/accounting/general-ledger/trial-balances/correction-audit?journalEntryType=${journalEntryType}`;
    if (postAccountingPeriodId != null) {
      url = url + `&postAccountingPeriodId=` + postAccountingPeriodId;
    }
    return this.http.post<any>(url, emptyBody);
  }

  getCostPools(year?: number): Observable<FiscalYearCostPool[]> {
    let url = `/finance/fy-cost-pool`;
    if (year) {
      url = url + `?year=` + year;
    }
    return this.http.get<FiscalYearCostPool[]>(`${url}`);
  }

  searchCostPools(costPoolSearch: CostPoolSearch): Observable<any> {
    let query: string = this.transform.buildSearchPanelURL(costPoolSearch);
    return this.http.get<any>(`/finance/search-cost-pool?` + query);
  }

  getIndirectRateSummary(costPoolId: number, indirectRateType: string, accountingPeriodId?: number): Observable<IndirectRateSummary[]> {
    let url = `/accounting/indirect-rate-summary?fiscalYearCostPoolId=${costPoolId}&indirectRateType=${indirectRateType}`;
    if (accountingPeriodId != null) {
      url = url + `&accountingPeriodId=${accountingPeriodId}`;
    }
    return this.http.get<IndirectRateSummary[]>(`${url}`);
  }

  searchStatementBalance(statementSearch: StatementSearch): Observable<any> {
    let query: string = this.transform.buildSearchPanelURL(statementSearch);
    return this.http.get<any>(`/accounting/general-ledger/account-reconciliation/search?` + query);
  }


  getAccountingCloseTaskStatus(closingType: string): Observable<AccountingCloseTaskStatus[]> {
    let url = `/accounting/accounting-close-status`;
    return this.http.get<AccountingCloseTaskStatus[]>(`${url}?accountingCloseType=${closingType}`);
  }

  updateAccountingCloseTaskStatus(accountingCloseTaskStatuses): Observable<AccountingCloseTaskStatus[]> {
    let url = `/accounting/accounting-close-status?`;
    return this.http.put<AccountingCloseTaskStatus[]>(url, accountingCloseTaskStatuses);
  }

  closeTasks(closingType: string, validationRequired: boolean): Observable<any> {
    let emptyBody: any;
    let url = `/accounting/accounting-close-status/close-period?accountingCloseType=${closingType}&validationRequired=${validationRequired}`;
    return this.http.post<any>(url, emptyBody);
  }

  generateClosingEntries(year: string): Observable<any> {
    let emptyBody: any;
    let url = `/accounting/generate-closing-entries?year=${year}`;
    return this.http.post<any>(url, emptyBody);
  }


  validateTasks(closingType: string): Observable<AccountingCloseTaskStatus[]> {
    let url = `/accounting/validate-accounting-close-status`;
    return this.http.get<AccountingCloseTaskStatus[]>(`${url}?accountingCloseType=${closingType}`);
  }


  checkTemplateNameUniqueness(name: string): Observable<QueryResult> {
    let url = `/accounting/journal-entry/unique-template?name=${name}`;
    return this.http.get<QueryResult>(url);
  }

  createFiscalYearLedgerAccountsAndFiscalYearCostPool(fiscalYear: string) {
    let url = `/accounting/fiscal-year-setup/accounts-costpools?year=${fiscalYear}`;
    return this.http.post<any>(url, {});
  }

  getGeneralLedgerDetailsPDF(accountingPeriodId: number) {
    let url = `/accounting/general-ledger-details-pdf`;
    if (accountingPeriodId != null) {
      url = url + `?accountingPeriodId=` + accountingPeriodId;
    }
    return this.http.get(url, { observe: 'response', responseType: BLOB_RESPONSE_TYPE });
  }

  getMonthAccountBalancesPDF(accountingPeriodId: number) {
    let url = `/accounting/month-account-balances-pdf`;
    if (accountingPeriodId != null) {
      url = url + `?accountingPeriodId=` + accountingPeriodId;
    }
    return this.http.get(url, { observe: 'response', responseType: BLOB_RESPONSE_TYPE });
  }

  getTrialBalancesPDF(accountingPeriodId: number, onlyLeafAccounts: boolean) {
    let url = `/accounting/trial-balance-pdf`;
    url = url + `?onlyLeafAccounts=` + onlyLeafAccounts;
    if (accountingPeriodId != null) {
      url = url + `&accountingPeriodId=` + accountingPeriodId;
    }

    return this.http.get(url, { observe: 'response', responseType: BLOB_RESPONSE_TYPE });
  }




  getIndirectExpensePDF() {
    let url = `/accounting/indirect-expense-statement-pdf`;
    return this.http.get(url, { observe: 'response', responseType: BLOB_RESPONSE_TYPE });
  }

  getJournalEntryPDFById(journalEntryId: number) {
    let url = `/accounting/journal-entry/pdf/${journalEntryId}`;
    return this.http.get(url, { observe: 'response', responseType: BLOB_RESPONSE_TYPE });
  }

  getJournalEntryPDF(journalEntry: JournalEntrySearch) {
    let query: string = this.transform.buildSearchPanelURL(journalEntry);
    return this.http.get<any>(`/accounting/journal-entry/pdfs?` + query, { observe: 'response', responseType: BLOB_RESPONSE_TYPE });
  }

  getAccountReconciliationPDF(statementSearch: StatementSearch): Observable<any> {
    let url = `/accounting/account-reconciliation-pdf?dateRange=${statementSearch.fromDate},${statementSearch.thruDate}&ledgerAccountId=${statementSearch.ledgerAccountId}`;
    return this.http.get(url, { observe: 'response', responseType: BLOB_RESPONSE_TYPE });

  }

  refreshBudgetHierarchy() {
    let url = `/accounting/general-ledger-refresh-budgets`;
    return this.http.post(url, {});
  }

  postInitialBalance() {
    let url = `/accounting/post-initial-balance`;
    return this.http.post(url, {});
  }

  getOrgUnitById(orgId?: number): Observable<any> {
    let url = `/solution/orgunits?orgId=${orgId}`;
    return this.http.get<any>(url);
  }

  postJournalEntries(journalEntries): Observable<any> {
    let url = `/accounting/post-journal-entries`;
    return this.http.post(url, journalEntries);
  }

  // CRUD - For Fiscal Year Rate Changes
  getProvisionalProvisonalRateChanges(): Observable<QueryResult> {
    let url = `/accounting/fiscal-year-rate-changes`;
    return this.http.get<QueryResult>(url);
  }

  saveProvisionalRateChange(rateChange: ProvisionalRateChange): Observable<any> {
    let url = `/accounting/fiscal-year-rate-changes`;
    return this.http.post<any>(url, rateChange);
  }

  updateProvisionalRateChange(rateChange: ProvisionalRateChange): Observable<any> {
    let url = `/accounting/fiscal-year-rate-changes/${rateChange.id}`;
    return this.http.put<any>(url, rateChange);
  }

  processProvisionalRateChange(rateChange: ProvisionalRateChange): Observable<any> {
    let url = `/accounting/fiscal-year-rate-changes/${rateChange.id}/process`;
    return this.http.put<any>(url, {});
  }

  reProcessProvisionalRateChange(rateChange: ProvisionalRateChange): Observable<ProvisionalRateChange> {
    let url = `/accounting/fiscal-year-rate-changes/${rateChange.id}/reprocess`;
    return this.http.put<ProvisionalRateChange>(url, {});
  }

  getProvisionalRates(rateChange: ProvisionalRateChange): Observable<QueryResult> {
    let url = `/accounting/fiscal-year-rate-changes/${rateChange.id}/provisional-rates`;
    return this.http.get<QueryResult>(url);
  }
}
